import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Hotel, Bed, UtensilsCrossed, MapPin } from 'lucide-react';
import './Gallery.css';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Gallery images organized by category
  const galleryData = {
    hotel: {
      title: 'Hotel Exterior & Ambiance',
      icon: Hotel,
      images: [
        {
          src: '/images/hero/hero1.jpg',
          title: 'Hotel The Container - Main Entrance',
          description: 'Unique container-based architecture creating a distinctive hospitality experience'
        },
        {
          src: '/images/hero/hero2.jpg',
          title: 'Container Design Details',
          description: 'Industrial charm meets modern comfort in our thoughtfully designed structure'
        },
        {
          src: '/images/hero/hero3.jpg',
          title: 'Hotel Exterior View',
          description: 'Sustainable architecture with a contemporary twist in the heart of Kolhapur'
        },
        {
          src: '/images/hero/hero4.jpg',
          title: 'Evening Ambiance',
          description: 'Beautiful lighting creates a warm and welcoming atmosphere for guests'
        },
        {
          src: '/images/hero/hero5.jpg',
          title: 'Hotel Premises',
          description: 'Well-maintained grounds with ample parking and easy accessibility'
        },
        {
          src: '/images/hero/hero6.jpg',
          title: 'Container Architecture',
          description: 'Innovative use of shipping containers creating eco-friendly accommodation'
        }
      ]
    },    rooms: {
      title: 'Container Rooms',
      icon: Bed,
      images: [
        {
          src: '/images/rooms/room1.jpg',
          title: 'Deluxe AC Container Room',
          description: 'Spacious and comfortable rooms with modern amenities, AC, attached bathroom with 24/7 hot water, and flat-screen TV'
        },
        {
          src: '/images/rooms/room2.jpg',
          title: 'Premier AC Container Room',
          description: 'Elegantly designed rooms featuring thematic interiors, premium bedding, and contemporary furnishings'
        },
        {
          src: '/images/rooms/room3.jpg',
          title: 'Super Deluxe Container Suite',
          description: 'Ultimate luxury with separate living area, premium furnishings, advanced amenities, and exclusive container elevation design'
        },
        {
          src: '/images/rooms/room4.jpg',
          title: 'Classic Container Room',
          description: 'Comfortable and affordable container room perfect for budget-conscious travelers with essential amenities'
        }
      ]
    },    facilities: {
      title: 'Restaurant & Facilities',
      icon: UtensilsCrossed,
      images: [
        {
          src: '/images/facilities/restaurant.jpg',
          title: 'In-House AC Restaurant',
          description: 'Authentic Kolhapuri cuisine featuring spicy veg and non-veg thali, tambda rassa, pandhra rassa, and traditional accompaniments'
        },
        {
          src: '/images/facilities/dining.jpg',
          title: 'Dining Experience',
          description: 'Comfortable dining area where guests can enjoy authentic Kolhapuri flavors in a modern container-themed setting'
        },
        {
          src: '/images/facilities/exterior.jpg',
          title: 'Hotel Premises & Parking',
          description: 'Well-maintained grounds with ample parking facility and easy accessibility for all guests'
        }
      ]
    },    attractions: {
      title: 'Nearby Attractions',
      icon: MapPin,
      images: [
        {
          src: '/images/attractions/mahalaxmi-temple.jpg',
          title: 'Mahalaxmi Temple',
          description: 'Ancient temple dedicated to Goddess Mahalaxmi, just 3.2 km from our hotel'
        },
        {
          src: '/images/attractions/panhala-fort.jpg',
          title: 'Panhala Fort',
          description: 'Historic hill fortress offering panoramic views, 18 km from the hotel'
        },
        {
          src: '/images/attractions/rankala-lake.jpg',
          title: 'Rankala Lake',
          description: 'Scenic artificial lake perfect for evening walks, 4.5 km away'
        },
        {
          src: '/images/attractions/new-palace.jpg',
          title: 'New Palace Museum',
          description: 'Former royal palace showcasing Kolhapur\'s heritage, 2.8 km from hotel'
        },        {
          src: '/images/attractions/narsinhwadi.jpeg',
          title: 'Narsoba Wadi',
          description: 'Sacred religious site and pilgrimage destination, 12 km from the hotel'
        },
        {
          src: '/images/attractions/Khidrapur-1.jpg',
          title: 'Khidrapur',
          description: 'Historic town with ancient Ganesha temple, 25 km from the hotel'
        },
        {
          src: '/images/attractions/dajipur-sanctuary.jpg',
          title: 'Dajipur Wildlife Sanctuary',
          description: 'Wildlife sanctuary famous for Indian bison, 65 km drive from hotel'
        }
      ]
    }
  };
  // Get all images for the lightbox (excluding attractions)
  const allImages = Object.entries(galleryData)
    .filter(([key]) => key !== 'attractions')
    .flatMap(([, category]) => category.images);
  
  // Filter images based on selected category
  const getFilteredImages = () => {
    if (selectedCategory === 'all') {
      return allImages;
    }
    return galleryData[selectedCategory]?.images || [];
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    const currentImages = getFilteredImages();
    const currentIndex = currentImages.findIndex(img => img.src === selectedImage.src);
    
    if (direction === 'next') {
      const nextIndex = (currentIndex + 1) % currentImages.length;
      setSelectedImage(currentImages[nextIndex]);
    } else {
      const prevIndex = (currentIndex - 1 + currentImages.length) % currentImages.length;
      setSelectedImage(currentImages[prevIndex]);
    }
  };

  const categories = [
    { key: 'all', label: 'All Photos', icon: null },
    { key: 'hotel', label: 'Hotel Exterior', icon: galleryData.hotel.icon },
    { key: 'rooms', label: 'Rooms', icon: galleryData.rooms.icon },
    { key: 'facilities', label: 'Restaurant', icon: galleryData.facilities.icon },
    { key: 'attractions', label: 'Attractions', icon: galleryData.attractions.icon }
  ];

  return (
    <div className="gallery-page">
      <div className="container">
        {/* Header */}
        <div className="gallery-header">
          <h1>Hotel Gallery</h1>
          <p>Explore Hotel The Container through our photo collection</p>
        </div>

        {/* Category Filter */}
        <div className="category-filter">
          {categories.map(category => (
            <button
              key={category.key}
              className={`filter-btn ${selectedCategory === category.key ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.key)}
            >
              {category.icon && <category.icon size={20} />}
              {category.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="gallery-grid">
          {getFilteredImages().map((image, index) => (
            <div
              key={index}
              className="gallery-item"
              onClick={() => openLightbox(image)}
            >
              <img src={image.src} alt={image.title} />
              <div className="gallery-item-overlay">
                <h3>{image.title}</h3>
                <p>{image.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Lightbox */}
        {selectedImage && (
          <div className="lightbox-overlay" onClick={closeLightbox}>
            <div className="lightbox-content" onClick={e => e.stopPropagation()}>
              <button className="lightbox-close" onClick={closeLightbox}>
                <X size={24} />
              </button>
              
              <button className="lightbox-nav prev" onClick={() => navigateImage('prev')}>
                <ChevronLeft size={24} />
              </button>
              
              <button className="lightbox-nav next" onClick={() => navigateImage('next')}>
                <ChevronRight size={24} />
              </button>
              
              <img src={selectedImage.src} alt={selectedImage.title} />
              
              <div className="lightbox-info">
                <h3>{selectedImage.title}</h3>
                <p>{selectedImage.description}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;
